import { useCallback, useMemo, useState } from 'react';
import _ from 'lodash';
import { useQuery } from '@tanstack/react-query';

import { usePushServiceSubscription } from 'services/PushService/PushService';
import useAPI from 'services/useAPI';
import createQueryRequest from 'services/createQueryRequest';

/**
 * Get a filtered list of alarms and receive updates from the server when new alarms arrive
 * @param filterOptions
 * @returns {filteredAlarms: [*], isLoading: boolean, hasError: boolean]}
 * @example
 * const [filteredAlarms, isLoadingAlarms, hasError] = useAlarmPushService(useMemo(() => ({ assetIds: [assetId] }), [assetId]))
 */
const useAlarmPushService = (filterOptions) => {
  const api = useAPI();
  const [liveAlarms, setLiveAlarms] = useState([]);
  const liveAlarmsQuery = useQuery(
    ['getAlarmsStatus'],
    createQueryRequest(api.core.getAlarmStatus),
    {
      onSuccess: useCallback((data) => {
        setLiveAlarms(data);
      }, []),
      initialData: [],
      refetchOnWindowFocus: false
    });

  const isLoading = liveAlarmsQuery.isLoading;
  const hasError = liveAlarms.isError;

  const alarmsUpdated = useCallback((pushData) => {
    const pushedAlarm = pushData.message;
    setLiveAlarms(currentAlarms => {
      if (!_.isEmpty(currentAlarms)) {
        const existingAlarmIndex = _.findIndex(currentAlarms, ['id', pushedAlarm.id]);

        if (existingAlarmIndex === -1) {
          // add new alarm
          currentAlarms = [...currentAlarms, pushedAlarm];
        } else if (pushedAlarm.status === 'inactive' && pushedAlarm.isAcknowledged) {
          // remove if inactive and acknowledged
          currentAlarms = [..._.reject(currentAlarms, alarm => alarm.id === pushedAlarm.id)];
        } else {
          // else just update
          currentAlarms[existingAlarmIndex] = pushedAlarm;
          currentAlarms = [...currentAlarms];
        }
      }

      return currentAlarms;
    });
  }, []);

  const filteredAlarms = useMemo(() => {
    let filteredAlarms = [...liveAlarms];

    if (filterOptions) {
      const assetIdFilter = filterOptions.assetIds;
      const alarmIdFilter = filterOptions.alarmIds;

      if (assetIdFilter) {
        filteredAlarms = filteredAlarms.filter(alarm => assetIdFilter.includes(alarm.entityId));
      }

      if (alarmIdFilter) {
        filteredAlarms = filteredAlarms.filter(alarm => alarmIdFilter.includes(alarm.id));
      }
    }
    return filteredAlarms;
  }, [liveAlarms, filterOptions]);

  const [, hasErrorInPushService] = usePushServiceSubscription(
    'alarms',
    alarmsUpdated,
    filterOptions?.assetIds);

  return [filteredAlarms, isLoading, hasError || hasErrorInPushService];
};

export { useAlarmPushService };
